// src/pages/Ads/AdsFetch.js
import axios from "axios";
import { format } from "date-fns";
import {
  apiRequestBodyBuilder,
  formatSegmentData,
} from "../../config/tableHelper";

import {
  AdDefaultColumns,
  AdSegmentDate,
  AdSegmentDeviceColumns,
  AdSegmentNetworkColumns,
  AdSegmentClickTypeColumns,
  AdSegmentTopVsOtherColumns,
  AdGroupColumns,
  AdCompareColumns,
  AdGroupDefaultColumns,
  AdGroupSegmentDate,
} from "./AdsColumns";

// --- Constants & Helpers ---

const API_BASE_URL = "https://api.confidanto.com";

function formatToMonthYearCustom(dateString) {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const date = new Date(dateString);
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${year}`;
}

const getSafeCustomerId = () => {
  const storedId = localStorage.getItem("customer_id");
  return !storedId || storedId === "Not Connected" ? "4643036315" : storedId;
};

/**
 * Centralized logic to format headlines, final_urls, and descriptions into HTML.
 */
const processAdDisplayContent = (item) => {
  const headlines = item.headlines ?? (item.headline ? [item.headline] : []);

  const finalUrls = Array.isArray(item.final_urls)
    ? item.final_urls
    : item.final_urls
    ? [item.final_urls]
    : [];

  const descriptions = [];
  if (item.description_1) descriptions.push(item.description_1);
  if (item.description_2) descriptions.push(item.description_2);

  const combinedHtml = [
    ...headlines.map(
      (h) => `<span class="text-blue-800 cursor-pointer"> ${h} |</span> `
    ),
    ...finalUrls.map(
      (u) =>
        `<br/> <span class="text-green-500 cursor-pointer"> ${u}</span> <br/>`
    ),
    ...descriptions.map((d) => `<span class="cursor-pointer"> ${d} </span>`),
  ].join(" ");

  return {
    ...item,
    headlines: combinedHtml,
  };
};

/**
 * Centralized logic to handle Date/Week/Month transformations.
 */
const processDateSegments = (data) => {
  return data.map((obj) => {
    const entries = Object.entries(obj);
    const transformedEntries = entries.map(([key, value]) => {
      if (key === "week" || key === "month") {
        let newValue = value;
        if (key === "month") {
          newValue = formatToMonthYearCustom(value);
        }
        return ["date", newValue];
      }
      return [key, value];
    });
    return Object.fromEntries(transformedEntries);
  });
};

// --- API Functions ---

export const fetchGroupData = async (
  state,
  setColumns,
  setData,
  setTotal,
  setError,
  selectedGroupIds,
  currentGroupType
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state, {
      group_ids: selectedGroupIds,
    });

    const response = await axios.post(
      `${API_BASE_URL}/ads-group/datewise-data`,
      requestBody
    );

    const groupData = response.data.map((item) => ({ ...item }));
    console.info("data: ", groupData);
    setData(groupData);

    const newCols = AdGroupDefaultColumns.map((col) => {
      if (col.key === "group_name" && currentGroupType !== null) {
        col.title = `${
          currentGroupType?.charAt(0).toUpperCase() + currentGroupType?.slice(1)
        } Group`;
      }
      return col;
    });

    setColumns(newCols);
    setTotal({});
  } catch (error) {
    setError(error.message);
    console.error("Error fetching group data:", error);
  }
};

export const fetchData = async (
  state,
  setColumns,
  setData,
  setTotal,
  setError
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state);

    const response = await axios.post(
      `${API_BASE_URL}/ads/datewise-data`,
      requestBody
    );

    // Use centralized formatter
    const combinedData = response.data.map(processAdDisplayContent);

    setData(combinedData);
    setColumns(AdDefaultColumns);
    setTotal({});
  } catch (error) {
    setError(error.message);
    console.error("Error fetching data:", error);
  }
};

export const fetchSegmentDateGroupData = async (
  state,
  setColumns,
  setData,
  setTotal,
  viewByObject,
  groupIds,
  currentGroupType
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state, {
      group_by: viewByObject.groupBy,
      group_ids: groupIds,
    });

    const res = await axios.post(
      `${API_BASE_URL}/ads-group/segment-data`,
      requestBody
    );

    // Use centralized date processor
    const resData = processDateSegments(res.data);

    setData(formatSegmentData(resData, viewByObject.groupBy));

    let newCols = AdGroupSegmentDate.map((item) => {
      if (["date", "week", "month"].includes(item.key)) {
        const segmentType = viewByObject.groupBy;
        item.title =
          segmentType.charAt(0).toUpperCase() +
          segmentType.slice(1).toLowerCase();
      }
      return item;
    });

    if (viewByObject.groupBy === "date") {
      newCols = newCols.map((item) =>
        item.key === "day_name" ? { ...item, visible: true } : item
      );
    }

    newCols = newCols.map((col) => {
      if (col.key === "group_name" && currentGroupType !== null) {
        col.title = `${
          currentGroupType?.charAt(0).toUpperCase() + currentGroupType?.slice(1)
        } Group`;
      }
      return col;
    });

    setColumns(newCols);
    setTotal({});
  } catch (err) {
    console.error("Error Fetching Ads Segment Group:", err);
  }
};

// Segments
export const fetchSegmentDateData = async (
  state,
  setColumns,
  setData,
  setTotal,
  viewByObject
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state, {
      group_by: viewByObject.groupBy,
    });

    const res = await axios.post(
      `${API_BASE_URL}/ads/segment-data`,
      requestBody
    );

    // 1. Process Dates
    let resData = processDateSegments(res.data);

    // 2. Process HTML Content
    resData = resData.map(processAdDisplayContent);

    setData(formatSegmentData(resData, viewByObject.groupBy));

    let newCols = AdSegmentDate.map((item) => {
      if (["date", "week", "month"].includes(item.key)) {
        const segmentType = viewByObject.groupBy;
        item.title =
          segmentType.charAt(0).toUpperCase() +
          segmentType.slice(1).toLowerCase();
      }
      return item;
    });

    if (viewByObject.groupBy === "date") {
      newCols = newCols.map((item) =>
        item.key === "day_name" ? { ...item, visible: true } : item
      );
    }

    setColumns(newCols);
    setTotal({});
  } catch (err) {
    console.error("Error Fetching Ads Segment Date:", err);
  }
};

export const fetchSegmentDeviceData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state);
    const response = await axios.post(
      `${API_BASE_URL}/ads/device`,
      requestBody
    );

    setColumns(AdSegmentDeviceColumns);

    const combinedData = response.data.map(processAdDisplayContent);

    setData(combinedData);
    setTotal({});
  } catch (error) {
    console.error("Error Fetching Ads Segment Device:", error);
  }
};

export const fetchSegmentNetworkData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    console.log("Network Segment", AdSegmentNetworkColumns);
    const customerId = getSafeCustomerId();
    const requestBody = apiRequestBodyBuilder(state, {
      customer_id: customerId,
    });

    const response = await axios.post(
      `${API_BASE_URL}/ads/network`,
      requestBody
    );

    setColumns(AdSegmentNetworkColumns);

    const combinedData = response.data.map(processAdDisplayContent);

    setData(combinedData);
    setTotal({});
  } catch (error) {
    console.error("Error Fetching Ads Segment Network:", error);
  }
};

export const fetchSegmentClickTypeData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    // Note: Originally logged Network Segment columns here, kept for consistency
    console.log("Click Type Segment", AdSegmentClickTypeColumns);
    const customerId = getSafeCustomerId();
    const requestBody = apiRequestBodyBuilder(state, {
      customer_id: customerId,
    });

    const response = await axios.post(
      `${API_BASE_URL}/ads/click-type`,
      requestBody
    );

    setColumns(AdSegmentClickTypeColumns);

    const combinedData = response.data.map(processAdDisplayContent);

    setData(combinedData);
    setTotal({});
  } catch (error) {
    console.error("Error Fetching Ads Segment Click Type:", error);
  }
};

export const fetchSegmentTopVsOtherData = async (
  state,
  setColumns,
  setData,
  setTotal
) => {
  try {
    const customerId = getSafeCustomerId();
    const requestBody = apiRequestBodyBuilder(state, {
      customer_id: customerId,
    });

    const response = await axios.post(
      `${API_BASE_URL}/ads/top-vs-other`,
      requestBody
    );

    setColumns(AdSegmentTopVsOtherColumns);

    const combinedData = response.data.map(processAdDisplayContent);

    setData(combinedData);
    setTotal({});
  } catch (error) {
    console.error("Error Fetching Ads Segment Top Vs Other:", error);
  }
};

// Compare
export const fetchAdsCompareData = async (
  state,
  setData,
  setShowDatePicker,
  setTotal,
  setColumns
) => {
  try {
    const requestBody = apiRequestBodyBuilder(state);
    const res = await axios.post(
      `${API_BASE_URL}/ads/datewise-data`,
      requestBody
    );

    setData(res.data);
    setShowDatePicker(false);
    setTotal({});
    setColumns(AdDefaultColumns);
  } catch (error) {
    console.error("Error fetching compare data:", error);
  }
};
